import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Calendar, ChevronRight, Activity, Droplet } from "lucide-react";
import { athleteListPrograms, athleteGetLactate } from "../../lib/api";
import { useAthleteAuth } from "../../lib/athleteAuth";
import { computeVdot, trainingPaces, paceRangeLabel } from "../../lib/vdot";
import CalendarSubscribe from "../../components/athlete/CalendarSubscribe";
import StravaMatchChooser from "../../components/athlete/StravaMatchChooser";
import AthleteNotificationBell from "../../components/athlete/AthleteNotificationBell";
import AthleteNav from "../../components/athlete/AthleteNav";
import InstallAppPrompt from "../../components/InstallAppPrompt";
import LockedProgramCard from "../../components/athlete/LockedProgramCard";

const TRAINING_PACE_ROWS = [
  { key: "easy", label: "Easy", note: "Recovery + base mileage" },
  { key: "marathon", label: "Marathon", note: "Long, sustained efforts" },
  { key: "threshold", label: "Threshold", note: "Tempo runs, cruise intervals" },
  { key: "interval", label: "Interval", note: "5K-pace VO2 work" },
  { key: "repetition", label: "Repetition", note: "Short fast strides + reps" },
];

function fmtRaceTime(secs) {
  if (!secs) return "—";
  const total = Math.round(Number(secs));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function AthleteHomePage() {
  const { athlete, loading, logout } = useAthleteAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [busy, setBusy] = useState(true);
  const [lactate, setLactate] = useState(null);

  useEffect(() => {
    if (!loading && !athlete) {
      navigate("/athlete/login", { replace: true });
      return;
    }
    if (athlete) {
      Promise.all([
        athleteListPrograms().catch(() => []),
        athleteGetLactate().catch(() => null),
      ])
        .then(([progs, lac]) => {
          setPrograms(progs || []);
          setLactate(lac);
        })
        .finally(() => setBusy(false));
    }
  }, [athlete, loading, navigate]);

  if (loading || !athlete) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  const profile = athlete || {};
  const pp = profile.pace_profile || {};
  const vdot = pp.race_distance_m && pp.race_time_seconds
    ? computeVdot(pp.race_distance_m, pp.race_time_seconds)
    : null;
  const paces = vdot ? trainingPaces(vdot) : null;

  return (
    <div className="min-h-screen bg-[var(--tir-ivory)]" data-testid="athlete-home">
      {/* Header */}
      <header className="border-b border-[var(--tir-border)] bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-navy)]">
              Athlete Portal
            </div>
            <div className="font-serif-display text-2xl text-[var(--tir-ink)] mt-0.5">
              {profile.name}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AthleteNotificationBell />
            <button
              onClick={logout}
              className="text-xs tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] inline-flex items-center gap-1.5"
              data-testid="athlete-logout"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <AthleteNav />

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 pb-24 md:pb-8 space-y-10">
        <InstallAppPrompt />
        {/* Strava double-day disambiguation. Self-hides when there's nothing
            pending; visible at the top so athletes don't miss it between
            weekly sync runs. */}
        <StravaMatchChooser />
        {/* HERO: Active programs. User explicitly called out this section
            getting lost on the page ("inline block that sticks out and
            clear") — promoted to a high-contrast card with a burgundy left
            accent, large League-Gothic title, and a visible CTA per program. */}
        <section data-testid="athlete-programs-hero">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif-display text-3xl text-[var(--tir-ink)] tracking-wide">
              Your Programs
            </h2>
            <span className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)]">
              {programs.length} active
            </span>
          </div>
          {busy ? (
            <div className="text-xs text-[var(--tir-muted)] py-6">Loading your programs…</div>
          ) : programs.length === 0 ? (
            <div className="bg-white rounded-lg border-2 border-dashed border-[var(--tir-border)] py-16 text-center px-6">
              <div className="font-serif-display text-2xl tracking-wide text-[var(--tir-ink)]">No active programs yet</div>
              <p className="text-sm text-[var(--tir-muted)] mt-2">
                Your coach hasn't assigned a program. They'll be in touch.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {programs.map((p) => {
                // Program is locked when the athlete's coaching payment is
                // not active. Backend fills in `paid_active` + `payment_link`
                // on every program returned by /athlete/me/programs.
                if (p.paid_active === false) {
                  return (
                    <LockedProgramCard
                      key={p.id}
                      paymentLink={p.payment_link}
                      programTitle={p.title}
                    />
                  );
                }
                return (
                <Link
                  key={p.id}
                  to={`/athlete/programs/${p.id}`}
                  data-testid={`athlete-program-card-${p.id}`}
                  className="group block bg-white rounded-lg border border-[var(--tir-border)] border-l-[6px] border-l-[var(--tir-burgundy)] hover:shadow-lg hover:border-[var(--tir-navy)] hover:border-l-[var(--tir-burgundy)] transition-all duration-200 p-6 sm:p-7"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2 font-mono-tir">
                        {p.target_race || "Training"}
                      </div>
                      <div className="font-serif-display text-3xl sm:text-4xl text-[var(--tir-ink)] leading-tight tracking-wide">
                        {p.title}
                      </div>
                      {p.goal && (
                        <p className="text-sm text-[var(--tir-muted)] mt-2 line-clamp-2 leading-relaxed">{p.goal}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-4 text-[11px] text-[var(--tir-muted)] font-mono-tir tracking-wide">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar size={12} /> {p.total_weeks} weeks
                        </span>
                        {p.start_date && (
                          <span>Started {new Date(p.start_date).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div className="inline-flex items-center gap-2 mt-4 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] group-hover:gap-3 transition-all">
                        Open program
                        <ChevronRight size={14} />
                      </div>
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Calendar subscription — only renders if there's at least one program */}
        {programs.length > 0 && <CalendarSubscribe />}

        {/* Profile snapshot */}
        <section>
          <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)] mb-3">
            Your profile
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white border border-[var(--tir-border)] p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Target race</div>
              <div className="font-serif-display text-lg mt-1">{profile.target_race || "—"}</div>
            </div>
            <div className="bg-white border border-[var(--tir-border)] p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">VDOT</div>
              <div className="font-serif-display text-lg mt-1">
                {vdot ? Math.round(vdot) : "—"}
              </div>
            </div>
            <div className="bg-white border border-[var(--tir-border)] p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Recent race</div>
              <div className="font-serif-display text-lg mt-1">
                {pp.race_label || (pp.race_distance_m ? `${(pp.race_distance_m / 1000).toFixed(1)} km` : "—")}
              </div>
              {pp.race_time_seconds && (
                <div className="text-[11px] text-[var(--tir-muted)] mt-0.5 font-mono-tir">
                  {fmtRaceTime(pp.race_time_seconds)}
                </div>
              )}
            </div>
            <div className="bg-white border border-[var(--tir-border)] p-4">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Last lactate test</div>
              <div className="font-serif-display text-lg mt-1">
                {lactate?.test_date ? new Date(lactate.test_date).toLocaleDateString() : "—"}
              </div>
              {lactate && (
                <div className="text-[11px] text-[var(--tir-muted)] mt-0.5">
                  LT2 HR {lactate?.lt2?.hr ? Math.round(lactate.lt2.hr) : "—"}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Training paces (VDOT-derived) */}
        {paces && (
          <section data-testid="athlete-training-paces">
            <div className="flex items-baseline gap-3 mb-3">
              <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)] inline-flex items-center gap-1.5">
                <Activity size={13} /> Training paces
              </h2>
              <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
                from VDOT {Math.round(vdot)}
              </span>
            </div>
            <div className="bg-white border border-[var(--tir-border)] divide-y divide-[var(--tir-border)]">
              {TRAINING_PACE_ROWS.map((row) => {
                const zone = paces[row.key];
                if (!zone) return null;
                return (
                  <div
                    key={row.key}
                    className="flex items-baseline gap-4 px-4 py-2.5"
                    data-testid={`pace-row-${row.key}`}
                  >
                    <div className="w-24 shrink-0">
                      <div className="text-sm font-semibold text-[var(--tir-ink)] capitalize">
                        {row.label}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 hidden md:block">
                      <div className="text-[11px] text-[var(--tir-muted)]">{row.note}</div>
                    </div>
                    <div className="font-mono-tir text-sm text-[var(--tir-burgundy)] font-semibold whitespace-nowrap">
                      {paceRangeLabel(zone)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Lactate test zones */}
        {lactate && Array.isArray(lactate.zones) && lactate.zones.length > 0 && (
          <section data-testid="athlete-lactate-zones">
            <div className="flex items-baseline gap-3 mb-3">
              <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)] inline-flex items-center gap-1.5">
                <Droplet size={13} /> Lactate zones
              </h2>
              <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
                tested {lactate.test_date ? new Date(lactate.test_date).toLocaleDateString() : "—"}
              </span>
            </div>
            <div className="bg-white border border-[var(--tir-border)] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tir-ivory)]">
                  <tr className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
                    <th className="text-left px-4 py-2">Zone</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">Name</th>
                    <th className="text-left px-4 py-2">HR</th>
                    <th className="text-left px-4 py-2">Pace</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">Speed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--tir-border)]">
                  {lactate.zones.map((z, i) => (
                    <tr key={i} data-testid={`zone-row-${z.zone_label || i}`}>
                      <td className="px-4 py-2 font-semibold text-[var(--tir-ink)]">
                        {z.zone_label || `Z${i + 1}`}
                      </td>
                      <td className="px-4 py-2 text-[var(--tir-ink)] hidden md:table-cell">
                        {z.name || "—"}
                      </td>
                      <td className="px-4 py-2 font-mono-tir text-[var(--tir-burgundy)]">
                        {z.hr_range || "—"}
                      </td>
                      <td className="px-4 py-2 font-mono-tir text-[var(--tir-burgundy)]">
                        {z.pace_range || "—"}
                      </td>
                      <td className="px-4 py-2 font-mono-tir text-[var(--tir-burgundy)] hidden md:table-cell">
                        {z.speed_range || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {lactate.coach_note && (
              <p className="text-xs text-[var(--tir-muted)] italic mt-2 max-w-2xl">
                "{lactate.coach_note}"
              </p>
            )}
          </section>
        )}

        {/* Programs were moved to the top of the page. */}
      </div>
    </div>
  );
}
